import { UnifiedEvent } from "@/components/PaginatedEventsList";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCountryFlag, getCountryName } from "@/data/countryData";
import { memo } from "react";
import { Clock, Globe, TrendingUp, ArrowUp, Star, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

// Currency to country mapping for fallback
const CURRENCY_COUNTRY_MAP: Record<string, string> = {
  'USD': 'US',
  'EUR': 'EU',
  'GBP': 'UK',
  'JPY': 'JP',
  'AUD': 'AU',
  'CAD': 'CA',
  'CHF': 'CH',
  'CNY': 'CN',
  'NZD': 'NZ',
};

interface EventListItemProps {
  event: UnifiedEvent;
  isSelected: boolean;
  onClick: (event: UnifiedEvent) => void;
  isToday?: boolean;
}

const EventListItemComponent = ({ event, isSelected, onClick, isToday = false }: EventListItemProps) => {
  const getImpactColor = (impact: string) => {
    switch (impact.toLowerCase()) {
      case 'high':
        return 'text-calendar-high';
      case 'medium':
        return 'text-calendar-medium';
      case 'low':
        return 'text-calendar-low';
      default:
        return 'text-muted-foreground';
    }
  };
  
  const getImpactBgColor = (impact: string) => {
    switch (impact.toLowerCase()) {
      case 'high':
        return 'bg-calendar-high/10 border-calendar-high/30';
      case 'medium':
        return 'bg-calendar-medium/10 border-calendar-medium/30';
      case 'low':
        return 'bg-calendar-low/10 border-calendar-low/30';
      default:
        return 'bg-muted/10 border-muted/30';
    }
  };

  const getImpactIcon = (impact: string) => {
    switch (impact.toLowerCase()) {
      case 'high':
        return <TrendingUp className="h-3 w-3" />;
      case 'medium':
        return <ArrowUp className="h-3 w-3" />;
      case 'low':
        return <Star className="h-3 w-3" />;
      default:
        return null;
    }
  };

  // Helper functions for comparison data
  const getVsEstimate = () => {
    if (event.actual === undefined || event.estimate === undefined || event.actual === null || event.estimate === null) {
      return null;
    }
    const diff = event.actual - event.estimate;
    if (diff === 0) return "";
    const symbol = diff > 0 ? "+" : "";
    return `${symbol}${diff.toFixed(2)}`;
  };

  const getVsPrevious = () => {
    if (event.actual === undefined || event.previous === undefined || event.actual === null || event.previous === null) {
      return null;
    }
    const diff = event.actual - event.previous;
    if (diff === 0) return "";
    const symbol = diff > 0 ? "+" : "";
    return `${symbol}${diff.toFixed(2)}`;
  };

  const getChangeColor = (value?: string | null) => {
    if (!value) return "text-gray-400";
    if (value.startsWith('+')) return "text-green-500";
    if (value.startsWith('-')) return "text-red-500";
    return "text-gray-400";
  };

  // Determine if the actual is better than estimate for the main display
  const actualBetterThanEstimate = event.actual && event.estimate ? event.actual > event.estimate : false;
  const actualEqualToEstimate = event.actual && event.estimate ? event.actual === event.estimate : false;
  const hasActualValue = event.actual !== undefined && event.actual !== null;
  const hasEstimateValue = event.estimate !== undefined && event.estimate !== null;
  const showActualComparison = hasActualValue && hasEstimateValue;

  const getChangeIcon = () => {
    if (!showActualComparison) return null;
    if (actualEqualToEstimate) return <Minus className="h-3 w-3 text-gray-400" />;
    return actualBetterThanEstimate 
      ? <ArrowUpRight className="h-3 w-3 text-green-500" /> 
      : <ArrowDownRight className="h-3 w-3 text-red-500" />;
  };

  const getChangeTextColor = () => {
    if (!showActualComparison || actualEqualToEstimate) return "";
    return actualBetterThanEstimate ? "text-green-500" : "text-red-500";
  };

  // Try to determine country from currency if country is missing
  const effectiveCountry = event.country || (event.currency ? CURRENCY_COUNTRY_MAP[event.currency] : '') || '';
  const flagUrl = getCountryFlag(effectiveCountry);
  const countryName = getCountryName(effectiveCountry);

  // Format event time if available
  const formatTime = (timeString?: string) => {
    if (!timeString) return null;
    try {
      const [hours, minutes] = timeString.split(':');
      const time = new Date();
      time.setHours(parseInt(hours, 10));
      time.setMinutes(parseInt(minutes, 10));
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return timeString;
    }
  };

  const formattedTime = formatTime(event.time);
  const vsEstimate = getVsEstimate();
  const vsPrevious = getVsPrevious();

  return (
    <button
      onClick={() => onClick(event)}
      className={cn(
        "w-full text-left p-4 rounded-lg border transition-all duration-300",
        "bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm",
        "hover:from-gray-900/60 hover:to-gray-800/60",
        isSelected 
          ? "border-primary/50 bg-primary-foreground/10 shadow-[0_0_10px_rgba(16,132,233,0.1)]" 
          : "border-white/5 hover:border-white/10",
        isToday && !isSelected && "border-blue-500/20 bg-blue-950/10"
      )}
    >
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-sm text-foreground break-words line-clamp-2">
                {event.title || "Untitled Event"}
              </h3>
            </div>
            
            <div className="flex flex-col gap-y-1.5 mb-0.5">
              {/* Country & Currency */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground overflow-hidden">
                {flagUrl ? (
                  <img 
                    src={flagUrl} 
                    alt={countryName}
                    className="h-3 w-3 object-cover rounded-sm flex-shrink-0"
                  />
                ) : (
                  <Globe className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">
                  {countryName || "Global"} - {event.currency || 'USD'}
                </span>
              </div>
              
              {/* Actual vs Estimate Indicator */}
              {showActualComparison && event.actual && event.estimate && (
                <div className={cn(
                  "flex items-center gap-1 text-xs overflow-hidden",
                  getChangeTextColor()
                )}>
                  {getChangeIcon()}
                  <span className="font-mono">
                    {event.actual.toFixed(1)} vs {event.estimate.toFixed(1)}
                  </span>
                </div>
              )}

              {/* Additional Event Data */}
              {hasActualValue && (
                <div className="text-xs text-white font-mono">
                  Actual: {event.actual?.toFixed(2)}
                </div>
              )}

              {/* vs Est and vs Prev */}
              <div className="flex gap-3 text-xs">
                {vsEstimate !== null && (
                  <div className="flex items-center gap-1">
                    <span className="text-white/60">vs Est:</span>
                    <span className={cn("font-mono", getChangeColor(vsEstimate))}>
                      {vsEstimate || "+"}
                    </span>
                  </div>
                )}
                {vsPrevious !== null && (
                  <div className="flex items-center gap-1">
                    <span className="text-white/60">vs Prev:</span>
                    <span className={cn("font-mono", getChangeColor(vsPrevious))}>
                      {vsPrevious}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Time if available */}
              {formattedTime && (
                <div className="flex items-center gap-1 text-xs text-blue-400/80 overflow-hidden">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  <span>{formattedTime}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge
              variant="outline"
              className={cn(
                "font-mono whitespace-nowrap",
                getImpactColor(event.impact)
              )}
            >
              {event.score.toFixed(2)}
            </Badge>
            
            {/* Impact indicator */}
            <div className={cn(
              "flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] leading-none",
              getImpactBgColor(event.impact),
              getImpactColor(event.impact)
            )}>
              {getImpactIcon(event.impact)}
              <span>{event.impact} Impact</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

// Implement proper comparison function for memo
const arePropsEqual = (prevProps: EventListItemProps, nextProps: EventListItemProps) => {
  return (
    prevProps.event.id === nextProps.event.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isToday === nextProps.isToday
  );
};

export const EventListItem = memo(EventListItemComponent, arePropsEqual);
